/**
 * Registro dos blocos ricos do post (E1.2) — FONTE ÚNICA.
 *
 * Um bloco é um `<div data-ds-block="tipo">` com HTML de verdade dentro, feito
 * das tags que o sanitizador já permite. Não existe JSON de props em atributo,
 * por três motivos, nesta ordem:
 *
 * 1. Segurança e robustez. O DOMPurify descarta o atributo inteiro quando o
 *    valor contém `<` — um texto com "5 > 3" perderia as props em silêncio, e
 *    "em silêncio" é o pior modo de falha possível aqui.
 * 2. O conteúdo fica sujeito ao mesmo sanitizador do resto do post, em vez de
 *    trafegar por um canal paralelo que precisaria da própria defesa.
 * 3. Degrada bem. Sem CSS, sem JS, sem o renderer — um `faq` ainda é um h3
 *    seguido de p. Isso é o que faz o SSR, a conversão para Markdown (E8.1) e
 *    o copiar-e-colar funcionarem de graça.
 *
 * Estilo é CSS puro por seletor de atributo (`[data-ds-block="callout"]`), em
 * `globals.css`: não há etapa de expansão do HTML, então o que o crawler
 * recebe do SSR e o que o React renderiza são literalmente o mesmo markup.
 * Interatividade (copiar, calcular) entra depois como enriquecimento
 * progressivo sobre esse mesmo HTML.
 */

export const BLOCK_TYPES = [
  "key-takeaways",
  "callout",
  "checklist",
  "faq",
  "table-compare",
  "cta-lead",
  "copy-block",
] as const;

export type BlockType = (typeof BLOCK_TYPES)[number];

/** Variantes do `callout`. Enum curto — nunca texto livre. */
export const CALLOUT_VARIANTS = ["info", "atencao", "regra"] as const;

/** Calculadoras embutidas (E1.3). O bloco existe aqui para já entrar na
 *  allowlist; o comportamento vem na task seguinte. */
export const CALCULATOR_KINDS = [
  "juros-parcelamento",
  "pdd",
  "roi-regua-cobranca",
] as const;

/**
 * Valores aceitos por atributo de bloco. O sanitizador recusa qualquer valor
 * fora daqui — bloco desconhecido perde o atributo e vira um `<div>` comum,
 * que ainda mostra o conteúdo. Falhar para "texto sem estilo" é aceitável;
 * falhar para "markup arbitrário estilizado" não seria.
 */
export const BLOCK_ATTR_VALUES: Record<string, readonly string[]> = {
  "data-ds-block": [...BLOCK_TYPES, "calculator"],
  "data-variant": CALLOUT_VARIANTS,
  "data-kind": CALCULATOR_KINDS,
};

export const BLOCK_ATTRS = Object.keys(BLOCK_ATTR_VALUES);

export const isValidBlockAttr = (name: string, value: string): boolean =>
  BLOCK_ATTR_VALUES[name]?.includes(value) ?? false;

/**
 * Forma esperada de cada bloco. Serve de documentação executável: alimenta o
 * prompt de redação (E5.2), as regras de conversão para Markdown (E8.1) e o
 * menu de inserção do editor.
 */
export const BLOCK_SHAPES: Record<BlockType | "calculator", string> = {
  "key-takeaways":
    "<div data-ds-block=\"key-takeaways\"><ul><li>…</li></ul></div> — 3 a 5 bullets logo após a introdução. É o trecho que os motores de resposta extraem.",
  callout:
    "<div data-ds-block=\"callout\" data-variant=\"info|atencao|regra\"><p>…</p></div> — aviso curto. `regra` é para exigência legal (CDC, LGPD).",
  checklist: "<div data-ds-block=\"checklist\"><ul><li>…</li></ul></div>",
  faq: "<div data-ds-block=\"faq\"><h3>Pergunta?</h3><p>Resposta.</p>…</div> — 4 a 6 pares, na forma exata em que as pessoas perguntam. Vira FAQPage JSON-LD (E1.6).",
  "table-compare":
    "<div data-ds-block=\"table-compare\"><table>…</table></div> — tabela comparativa; o wrapper existe para o estilo e para a conversão em Markdown.",
  "cta-lead":
    "<div data-ds-block=\"cta-lead\"><p>…</p><a href=\"/crediario\">…</a></div> — caixa de conversão apontando para a LP do cluster.",
  "copy-block":
    "<div data-ds-block=\"copy-block\"><p>…</p></div> — texto pronto para o lojista copiar (ex.: modelo de mensagem de cobrança).",
  calculator:
    "<div data-ds-block=\"calculator\" data-kind=\"…\"><table>…</table></div> — a tabela de exemplo é o conteúdo indexável; os campos interativos entram por cima dela no cliente (E1.3).",
};
